// src/api/reglagesGlobaux.api.ts — SCE Issues
// La ligne des réglages GLOBAUX de SCE_UserPrefs (`Title = '__global__'`),
// partagée par plusieurs réglages : couleurs métier (Lot 8), modèles choisis à
// la main à l'import Windchill (Lot 18).
//
// Le JSON de la colonne Prefs porte un bloc par réglage. Chaque écriture
// RELIT la ligne et ne remplace que son bloc : les autres restent intacts.
// Un UPN contient toujours « @ » : aucune collision possible avec la ligne
// d'un utilisateur, et la présence (listerPresents) l'écarte pour la même raison.

import { graphGet, graphPatch, graphPost } from './graphClient'

const SITE_ID = import.meta.env.VITE_SHAREPOINT_SITE_ID ?? ''
const LIST_ID = import.meta.env.VITE_LIST_USERPREFS_ID ?? ''

export const CLE_REGLAGES_GLOBAUX = '__global__'

/** Chemin Graph de SCE_UserPrefs. */
export function cheminReglages(): string {
  return listPath()
}

/** Faux en test ou sans configuration : rien à lire. */
export function reglagesConfigures(): boolean {
  return SITE_ID !== '' && LIST_ID !== ''
}

function listPath(): string {
  return `/sites/${SITE_ID}/lists/${LIST_ID}`
}

export interface LigneGlobale {
  itemId: string | null
  /** Tout le JSON de la ligne, pour réécrire les autres blocs à l'identique. */
  json: Record<string, unknown>
}

export async function lireLigneGlobale(): Promise<LigneGlobale> {
  const filtre = `fields/Title eq '${CLE_REGLAGES_GLOBAUX}'`
  const chemin =
    `${listPath()}/items?$expand=fields($select=Title,Prefs)` +
    `&$filter=${encodeURIComponent(filtre)}&$top=1`
  const reponse = await graphGet<{
    value: { id: string; fields: { Prefs?: string } }[]
  }>(chemin)
  const item = reponse.value[0]
  if (!item) return { itemId: null, json: {} }

  let json: Record<string, unknown> = {}
  try {
    const brut: unknown = JSON.parse(item.fields.Prefs ?? '')
    if (brut && typeof brut === 'object' && !Array.isArray(brut)) {
      json = brut as Record<string, unknown>
    }
  } catch {
    // Ligne vide ou JSON abîmé : couleurs d'origine, et la prochaine
    // écriture repartira d'un JSON sain.
  }
  return { itemId: item.id, json }
}

/**
 * Réécrit UN bloc du JSON de la ligne globale à partir de sa valeur relue à
 * l'instant (une lecture, une écriture) — les autres blocs sont réécrits à
 * l'identique. Crée la ligne si elle n'existe pas.
 */
export async function modifierBlocGlobal(nom: string, calculer: (actuel: unknown) => unknown): Promise<void> {
  const ligne = await lireLigneGlobale()
  const prefs = JSON.stringify({ ...ligne.json, [nom]: calculer(ligne.json[nom]) })
  if (ligne.itemId) {
    await graphPatch<unknown>(`${listPath()}/items/${ligne.itemId}/fields`, { Prefs: prefs })
  } else {
    await graphPost<unknown>(`${listPath()}/items`, {
      fields: { Title: CLE_REGLAGES_GLOBAUX, Prefs: prefs },
    })
  }
}

/** Lit un bloc de la ligne globale ; undefined si la liste n'est pas configurée ou le bloc absent. */
export async function lireBlocGlobal(nom: string): Promise<unknown> {
  if (!SITE_ID || !LIST_ID) return undefined
  const { json } = await lireLigneGlobale()
  return json[nom]
}
